import { createDedupeStrategy, type GlobalRequestInfoCache, type RequestInfoCache } from "./dedupe";
import {
	executeHooks,
	executeHooksInCatchBlock,
	type CallApiExtraOptionsForHooks,
	type CallApiRequestOptionsForHooks,
	type ErrorContext,
	type ExecuteHookInfo,
	type RequestContext,
	type ResponseContext,
	type SuccessContext,
	type ValidationErrorContext,
} from "./hooks";
import { initializePlugins, type CallApiPlugin } from "./plugins";
import { createRefetchManager } from "./refetch";
import {
	getCustomizedErrorResult,
	resolveErrorResult,
	resolveResponseData,
	resolveSuccessResult,
	type ErrorInfo,
	type GetResponseType,
	type ResponseTypeType,
	type ResultModeType,
} from "./result";
import { createRetryManager } from "./retry";
import type {
	BaseCallApiConfig,
	BaseCallApiExtraOptions,
	CallApiConfig,
	CallApiContext,
	CallApiExtraOptions,
	CallApiRequestOptions,
	CallApiResult,
	GetBaseSchemaConfig,
	GetBaseSchemaRoutes,
} from "./types/common";
import type {
	GetCurrentRouteSchema,
	GetCurrentRouteSchemaKey,
	InferInitURL,
	ThrowOnErrorBoolean,
} from "./types/conditional-types";
import type {
	DefaultCallApiContext,
	DefaultPluginArray,
	DefaultThrowOnError,
} from "./types/default-types";
import type { AnyFunction } from "./types/type-helpers";
import { getFullAndNormalizedURL } from "./url";
import {
	createCombinedSignal,
	createTimeoutSignal,
	getBody,
	getFetchImpl,
	getHeaders,
	getMethod,
	getResolvedHeaders,
	splitConfig,
} from "./utils/common";
import { HTTPError } from "./utils/external/error";
import { isHTTPErrorInstance, isValidationErrorInstance } from "./utils/external/guards";
import { isFunction } from "./utils/guards";
import {
	getCurrentRouteSchemaKeyAndMainInitURL,
	handleConfigValidation,
	handleSchemaValidation,
	type BaseCallApiSchemaAndConfig,
	type BaseCallApiSchemaRoutes,
	type CallApiSchema,
	type CallApiSchemaConfig,
	type InferSchemaOutput,
} from "./validation";

const $GlobalRequestInfoCache: GlobalRequestInfoCache = new Map();

export const createFetchClientWithContext = <
	TOuterCallApiContext extends CallApiContext = DefaultCallApiContext,
>() => {
	const createFetchClient = <
		TBaseCallApiContext extends CallApiContext = TOuterCallApiContext,
		TBaseData = TBaseCallApiContext["Data"],
		TBaseErrorData = TBaseCallApiContext["ErrorData"],
		TBaseResultMode extends ResultModeType = TBaseCallApiContext["ResultMode"] extends ResultModeType ?
			TBaseCallApiContext["ResultMode"]
		:	DefaultCallApiContext["ResultMode"],
		TBaseThrowOnError extends ThrowOnErrorBoolean = DefaultThrowOnError,
		TBaseResponseType extends ResponseTypeType = ResponseTypeType,
		const TBaseSchemaAndConfig extends BaseCallApiSchemaAndConfig = BaseCallApiSchemaAndConfig,
		const TBasePluginArray extends CallApiPlugin[] = DefaultPluginArray,
		TComputedBaseSchemaConfig extends CallApiSchemaConfig = GetBaseSchemaConfig<TBaseSchemaAndConfig>,
		TComputedBaseSchemaRoutes extends BaseCallApiSchemaRoutes =
			GetBaseSchemaRoutes<TBaseSchemaAndConfig>,
	>(
		initBaseConfig: BaseCallApiConfig<
			TBaseCallApiContext,
			TBaseData,
			TBaseErrorData,
			TBaseResultMode,
			TBaseThrowOnError,
			TBaseResponseType,
			TBaseSchemaAndConfig,
			TBasePluginArray
		> = {} as never
	) => {
		const $LocalRequestInfoCache: RequestInfoCache = new Map();

		const callApi = async <
			TData = TBaseData,
			TErrorData = TBaseErrorData,
			TResultMode extends ResultModeType = TBaseResultMode,
			TCallApiContext extends CallApiContext = TBaseCallApiContext,
			TThrowOnError extends ThrowOnErrorBoolean = TBaseThrowOnError,
			TResponseType extends ResponseTypeType = TBaseResponseType,
			const TSchemaConfig extends CallApiSchemaConfig = TComputedBaseSchemaConfig,
			TInitURL extends InferInitURL<TComputedBaseSchemaRoutes, TSchemaConfig> = InferInitURL<
				TComputedBaseSchemaRoutes,
				TSchemaConfig
			>,
			TCurrentRouteSchemaKey extends GetCurrentRouteSchemaKey<TSchemaConfig, TInitURL> =
				GetCurrentRouteSchemaKey<TSchemaConfig, TInitURL>,
			const TSchema extends CallApiSchema = GetCurrentRouteSchema<
				TComputedBaseSchemaRoutes,
				TCurrentRouteSchemaKey
			>,
			const TPluginArray extends CallApiPlugin[] = TBasePluginArray,
			TComputedData = InferSchemaOutput<TSchema["data"], GetResponseType<TData, TResponseType>>,
			TComputedErrorData = InferSchemaOutput<
				TSchema["errorData"],
				GetResponseType<TErrorData, TResponseType>
			>,
			TComputedResult = CallApiResult<TComputedData, TComputedErrorData, TResultMode, TThrowOnError>,
		>(
			initURL: TInitURL,
			initConfig: CallApiConfig<
				TCallApiContext,
				TComputedData,
				TComputedErrorData,
				TResultMode,
				TThrowOnError,
				TResponseType,
				TComputedBaseSchemaRoutes,
				TSchema,
				TComputedBaseSchemaConfig,
				TSchemaConfig,
				TInitURL,
				TCurrentRouteSchemaKey,
				TBasePluginArray,
				TPluginArray
			> = {} as never
		): Promise<TComputedResult> => {
			const [fetchOptions, extraOptions] = splitConfig<CallApiExtraOptions>(initConfig);

			const resolvedBaseConfig =
				isFunction(initBaseConfig) ?
					initBaseConfig({
						initURL: initURL.toString(),
						options: extraOptions,
						request: fetchOptions,
					})
				:	initBaseConfig;

			const baseConfig = resolvedBaseConfig as Exclude<BaseCallApiConfig, AnyFunction>;
			const config = initConfig as CallApiConfig;

			// eslint-disable-next-line ts-eslint/no-unnecessary-type-arguments -- False Positive
			const [baseFetchOptions, baseExtraOptions] = splitConfig<BaseCallApiExtraOptions>(baseConfig);

			const shouldSkipAutoMergeForOptions =
				baseExtraOptions.skipAutoMergeFor === "all" || baseExtraOptions.skipAutoMergeFor === "options";

			const shouldSkipAutoMergeForRequest =
				baseExtraOptions.skipAutoMergeFor === "all" || baseExtraOptions.skipAutoMergeFor === "request";

			// == Merged Extra Options
			const mergedExtraOptions = {
				...baseExtraOptions,
				...(!shouldSkipAutoMergeForOptions && extraOptions),
			};

			// == Merged Request Options
			const mergedRequestOptions = {
				...baseFetchOptions,
				...baseFetchOptions.extraFetchOptions,
				...(!shouldSkipAutoMergeForRequest && fetchOptions),
				...(!shouldSkipAutoMergeForRequest && fetchOptions.extraFetchOptions),
			} satisfies CallApiRequestOptions;

			const initURLResult = getCurrentRouteSchemaKeyAndMainInitURL({
				baseExtraOptions: baseConfig,
				extraOptions: config,
				initURL: initURL.toString(),
			});

			const {
				resolvedCurrentRouteSchemaKey,
				resolvedHooks,
				resolvedInitURL,
				resolvedMiddlewares,
				resolvedOptions,
				resolvedRequest,
			} = await initializePlugins({
				baseConfig,
				config,
				...initURLResult,
				options: mergedExtraOptions as CallApiExtraOptionsForHooks,
				request: {
					...mergedRequestOptions,
					headers: getResolvedHeaders({ baseHeaders: baseConfig.headers, headers: config.headers }),
					method: getMethod({ initURL: initURLResult.initURL, method: mergedRequestOptions.method }),
				} as CallApiRequestOptionsForHooks,
			});

			const { fullURL, normalizedInitURL } = getFullAndNormalizedURL({
				baseURL: resolvedOptions.baseURL,
				initURL: resolvedInitURL,
				params: resolvedOptions.params,
				query: resolvedOptions.query,
			});

			const options = {
				...resolvedOptions,
				...resolvedHooks,
				...resolvedMiddlewares,
				fullURL,
				initURL: resolvedInitURL,
				initURLNormalized: normalizedInitURL,
			} satisfies CallApiExtraOptions;

			const refetchFnResult = createRefetchManager({
				callApi,
				callApiArgs: { config, initURL },
				options,
			});

			Object.assign(options, refetchFnResult);

			const newFetchController = new AbortController();

			const timeoutSignal = createTimeoutSignal(options.timeout);

			const combinedSignal = createCombinedSignal(
				timeoutSignal,
				resolvedRequest.signal,
				newFetchController.signal
			);

			const request = {
				...resolvedRequest,
				signal: combinedSignal,
			} satisfies CallApiRequestOptionsForHooks;

			const {
				getAbortErrorMessage,
				handleRequestCancelStrategy,
				handleRequestDeferStrategy,
				removeDedupeKeyFromCache,
				resolvedDedupeStrategy,
			} = await createDedupeStrategy({
				$GlobalRequestInfoCache,
				$LocalRequestInfoCache,
				baseConfig,
				config,
				newFetchController,
				options,
				request,
			});

			try {
				handleRequestCancelStrategy();

				await executeHooks(options.onRequest?.({ baseConfig, config, options, request }));

				const {
					extraOptionsValidationResult,
					requestOptionsValidationResult,
					resolvedSchema,
					resolvedSchemaConfig,
				} = await handleConfigValidation({
					baseExtraOptions,
					currentRouteSchemaKey: resolvedCurrentRouteSchemaKey,
					extraOptions,
					options,
					request,
				});

				// == Apply Schema Output for Extra Options

				Object.assign(options, extraOptionsValidationResult);

				const modifiedRequestOptionsValidationResult = {
					body: getBody({
						body: requestOptionsValidationResult.body,
						bodySerializer: options.bodySerializer,
						resolvedHeaders: requestOptionsValidationResult.headers,
					}),
					headers: await getHeaders({
						auth: options.auth,
						body: requestOptionsValidationResult.body,
						resolvedHeaders: requestOptionsValidationResult.headers,
					}),
					method: getMethod({
						initURL: resolvedInitURL,
						method: requestOptionsValidationResult.method,
					}),
				} satisfies CallApiRequestOptionsForHooks;

				// == Apply Schema Output for Request Options
				Object.assign(request, modifiedRequestOptionsValidationResult);

				const readyRequestContext = {
					baseConfig,
					config,
					options,
					request,
				} satisfies RequestContext;

				await executeHooks(options.onRequestReady?.(readyRequestContext));

				const fetchApi = getFetchImpl({
					customFetchImpl: options.customFetchImpl,
					fetchMiddleware: options.fetchMiddleware,
					requestContext: readyRequestContext,
				});

				const response = await handleRequestDeferStrategy({ fetchApi, options, request });

				// == Also clone response when dedupeStrategy is set to "defer" to avoid error thrown from reading response.(whatever) more than once
				const shouldCloneResponse = Boolean(
					resolvedDedupeStrategy === "defer" || options.cloneResponse
				);

				const responseData = await resolveResponseData({
					response: shouldCloneResponse ? response.clone() : response,
					responseParser: options.responseParser,
					responseType: options.responseType,
					resultMode: options.resultMode,
				});

				if (!response.ok) {
					const validErrorData = await handleSchemaValidation(resolvedSchema, "errorData", {
						inputValue: responseData,
						response,
						resultMode: options.resultMode,
						schemaConfig: resolvedSchemaConfig,
					});

					// == Push all error handling responsibilities to the catch block if not retrying
					throw new HTTPError(
						{
							defaultHTTPErrorMessage: options.defaultHTTPErrorMessage,
							errorData: validErrorData,
							response,
						},
						{ cause: validErrorData }
					);
				}

				const validSuccessData = await handleSchemaValidation(resolvedSchema, "data", {
					inputValue: responseData,
					response,
					resultMode: options.resultMode,
					schemaConfig: resolvedSchemaConfig,
				});

				const successContext = {
					baseConfig,
					config,
					data: validSuccessData,
					options,
					request,
					response,
				} satisfies SuccessContext;

				await executeHooks(
					options.onSuccess?.(successContext),
					options.onResponse?.({ ...successContext, error: null })
				);

				const successResult = resolveSuccessResult(successContext.data, {
					response: successContext.response,
					resultMode: options.resultMode,
				});

				return successResult as never;

				// == Exhaustive Error handling
			} catch (error) {
				const errorInfo = {
					cloneResponse: options.cloneResponse,
					resultMode: options.resultMode,
				} satisfies ErrorInfo;

				const { errorDetails, errorResult } = resolveErrorResult(error, errorInfo);

				const errorContext = {
					baseConfig,
					config,
					error: errorDetails.error as never,
					options,
					request,
					response: errorDetails.response as never,
				} satisfies ErrorContext;

				const shouldThrowOnError = Boolean(
					isFunction(options.throwOnError) ? options.throwOnError(errorContext) : options.throwOnError
				);

				const hookInfo = {
					errorInfo,
					shouldThrowOnError,
				} satisfies ExecuteHookInfo;

				const { handleRetryOrGetErrorResult } = createRetryManager({
					callApi,
					callApiArgs: { config, initURL },
					error,
					errorContext,
					errorResult,
					hookInfo,
				});

				const responseContext: ResponseContext | null =
					(errorContext.response as Response | null) ? { ...errorContext, data: null } : null;

				if (isValidationErrorInstance(error)) {
					const hookError = await executeHooksInCatchBlock(
						[
							responseContext && options.onResponse?.(responseContext),
							options.onValidationError?.(errorContext as ValidationErrorContext),
							options.onError?.(errorContext),
						],
						hookInfo
					);

					return (hookError ?? (await handleRetryOrGetErrorResult())) as never;
				}

				if (isHTTPErrorInstance<TErrorData>(error)) {
					const hookError = await executeHooksInCatchBlock(
						[
							responseContext && options.onResponse?.(responseContext),
							options.onResponseError?.(errorContext),
							options.onError?.(errorContext),
						],
						hookInfo
					);

					return (hookError ?? (await handleRetryOrGetErrorResult())) as never;
				}

				let message = (error as Error | undefined)?.message;

				if (error instanceof DOMException && error.name === "AbortError") {
					message = getAbortErrorMessage();

					!shouldThrowOnError && console.error(`${error.name}:`, message);
				}

				if (error instanceof DOMException && error.name === "TimeoutError") {
					message = `Request timed out after ${options.timeout}ms`;

					!shouldThrowOnError && console.error(`${error.name}:`, message);
				}

				const hookError = await executeHooksInCatchBlock(
					[
						responseContext && options.onResponse?.(responseContext),
						options.onRequestError?.(errorContext),
						options.onError?.(errorContext),
					],
					hookInfo
				);

				return (hookError
					?? getCustomizedErrorResult(await handleRetryOrGetErrorResult(), { message })) as never;

				// == Removing the now unneeded AbortController from store
			} finally {
				removeDedupeKeyFromCache();
			}
		};

		return callApi;
	};

	return createFetchClient;
};

export const createFetchClient = createFetchClientWithContext();

export const callApi = createFetchClient();
